import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { object, string } from 'yup';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import Button from 'shared/components/Button/Button';
import Input from 'shared/components/Input/Input';
import {
	apiDeactivateUser,
	updateUserPassword,
	updateUserProperties,
} from 'shared/modules/user/actions';
import { doFetchUserProperties } from 'shared/new/authUtils';
import { UserContext } from 'shared/Providers/User';
import { prettyDate } from 'shared/utils/format';
import LocalSettings from 'app/components/LocalSettings/LocalSettings';
import { PageContainer } from 'shared/Pages/common/PageContainer';
import styles from 'shared/Pages/common/Account/account.module.scss';
import { UserStats } from 'shared/Pages/common/Account/UserStats';
import { useUserData } from 'shared/Providers/UserData';

export default function Account() {
	const {
		permissions: { canUseApp, canManageAccount },
	} = useContext(UserContext);

	const dispatch = useDispatch();

	const { user } = useContext(UserContext);
	const {
		user: { created },
	} = useUserData();

	const [edit, setEdit] = useState(false);
	const editMode = useMemo(() => edit && canUseApp, [edit, canUseApp]);

	const toggleEdit = useCallback(
		(resetForm?: () => void) => {
			if (edit && resetForm) {
				resetForm();
			}
			setEdit(!edit);
		},
		[edit]
	);

	const handleSubmit = useCallback(
		async (values: { name: string; email: string; newPassword: string; password: string }) => {
			if (user?.name !== values.name || user?.email !== values.email) {
				await updateUserProperties(values);
				await dispatch(doFetchUserProperties());
			}

			if (!!values.newPassword && !!values.password) {
				await updateUserPassword(values);
			}

			toggleEdit();
		},
		[user?.name, user?.email, dispatch, toggleEdit]
	);

	const handleDelete = useCallback(async () => {
		const answer = window.confirm(
			'Be careful:\n' +
				'The current user will be deactivated and you WILL NOT BE ABLE to log in.\n' +
				'Continue?'
		);

		if (answer) {
			await dispatch(apiDeactivateUser(user?.id));
		}
	}, [user?.id, dispatch]);

	const initValues = useMemo(
		() => ({
			name: user!.name ?? '',
			email: user!.email ?? '',
			newPassword: '',
			password: '',
		}),
		[user?.name, user?.email]
	);

	return (
		<PageContainer parentTitle="Account" title={user?.name}>
			<Formik
				initialValues={initValues}
				validationSchema={object().shape({
					name: string().required('Required'),
					email: string().email('Invalid email address').required('Required'),
					// phone: string().required('Required')
				})}
				enableReinitialize
				onSubmit={handleSubmit}
				render={({ resetForm }) => (
					<Form>
						<div className={styles.editContainer}>
							<div>
								<Field
									label="Created"
									name="created"
									readOnly
									component={Input}
									labelWidth={Input.LABEL_WIDTH.FULL}
									field={{ value: prettyDate(created) }}
									spaceBottom={Input.SPACE_BOTTOM.MEDIUM}
								/>
								<Field
									label="Full Name"
									name="name"
									component={Input}
									readOnly={!editMode}
									editClick={toggleEdit}
									labelWidth={Input.LABEL_WIDTH.FULL}
									spaceBottom={Input.SPACE_BOTTOM.MEDIUM}
								/>
								<div className={styles.horizontal}>
									<Field
										label="Email"
										name="email"
										placeholder="example@domain.com"
										component={Input}
										readOnly={!editMode}
										editClick={toggleEdit}
										labelWidth={Input.LABEL_WIDTH.FULL}
										spaceBottom={Input.SPACE_BOTTOM.MEDIUM}
									/>
								</div>
								{editMode && (
									<div className={styles.horizontal}>
										<Field
											label="New Password"
											name="newPassword"
											type={Input.TYPE.PASSWORD}
											component={Input}
											labelWidth={Input.LABEL_WIDTH.FULL}
											spaceBottom={Input.SPACE_BOTTOM.MEDIUM}
										/>
										<Field
											label="Old Password"
											name="password"
											type={Input.TYPE.PASSWORD}
											component={Input}
											labelWidth={Input.LABEL_WIDTH.FULL}
											spaceBottom={Input.SPACE_BOTTOM.MEDIUM}
										/>
									</div>
								)}
							</div>
							<div className={styles.saveWrapper}>
								{editMode && (
									<Button small type="submit" className={styles.toggleEdit}>
										Save
									</Button>
								)}
								{editMode && (
									<Button
										small
										color={Button.COLOR.WARNING}
										onClick={handleDelete}
										className={styles.toggleEdit}
									>
										Delete
									</Button>
								)}
								{canUseApp && (
									<Button
										small
										color={editMode ? Button.COLOR.GRAY : Button.COLOR.PRIMARY}
										onClick={() => toggleEdit(resetForm)}
										className={styles.toggleEdit}
									>
										{editMode ? 'Cancel' : 'Edit'}
									</Button>
								)}
							</div>
						</div>
					</Form>
				)}
			/>
			<PageContainer title="Roles" size="medium">
				<div className={styles.fields}>
					{user?.roles.map(({ key, name }) => (
						<Input
							key={key}
							labelOnTheRight
							label={name}
							field={{
								name: `roles.${key}`,
								value: true,
							}}
							type={Input.TYPE.SWITCH}
							spaceBottom={Input.SPACE_BOTTOM.SMALL}
							readOnly
						/>
					))}
				</div>
			</PageContainer>
			{canManageAccount && <LocalSettings showTitle />}
			<UserStats />
		</PageContainer>
	);
}
